import { useState } from 'react'
import {
  Card, Table, Button, Tag, Space, Typography, Form, Select, DatePicker,
  Input, InputNumber, Switch, Row, Col, Drawer, message, Tabs,
} from 'antd'
import { PlusOutlined, EditOutlined, ThunderboltOutlined, AlertOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { productionLogsApi } from '@/features/reports/api'
import apiClient from '@/shared/api/client'
import { filterActiveShifts } from '@/shared/constants/shifts'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography

const DEPARTMENTS = [
  'BLOWROOM', 'CARDING', 'BREAKER_DRAW', 'FINISHER_DRAW',
  'COMBING', 'ROVING', 'RING_SPINNING', 'AUTO_WINDING', 'TFO',
]

const BD_CATEGORIES = [
  { value: 'M', label: 'Mechanical' },
  { value: 'E', label: 'Electrical' },
  { value: 'Q', label: 'Quality Hold' },
  { value: 'P', label: 'Material Pending' },
  { value: 'U', label: 'Utility' },
  { value: 'O', label: 'Operator Absent' },
  { value: 'C', label: 'Cleaning' },
  { value: 'S', label: 'Setup/Changeover' },
]

const WASTE_TYPES = [
  { value: 'HW', label: 'Hard Waste' },
  { value: 'SW', label: 'Soft Waste' },
  { value: 'PF', label: 'Pneumafil' },
  { value: 'NO', label: 'Noil' },
  { value: 'RW', label: 'Rework Waste' },
  { value: 'FL', label: 'Fly/Cleaning' },
  { value: 'BR', label: 'Blowroom Droppings' },
]

export default function ProductionLogPage() {
  const { plantId } = usePlantId()
  const qc = useQueryClient()

  const [openLog, setOpenLog] = useState(false)
  const [openBD, setOpenBD] = useState(false)
  const [openWaste, setOpenWaste] = useState(false)
  const [editingLog, setEditingLog] = useState<Record<string, unknown> | null>(null)
  const [form] = Form.useForm()
  const [bdForm] = Form.useForm()
  const [wasteForm] = Form.useForm()

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['production-logs-list', plantId],
    queryFn: () => productionLogsApi.listLogs({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  const { data: breakdowns, isLoading: bdLoading } = useQuery({
    queryKey: ['breakdowns-list', plantId],
    queryFn: () => productionLogsApi.listBreakdowns({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  const { data: wasteRecords, isLoading: wasteLoading } = useQuery({
    queryKey: ['waste-records-list', plantId],
    queryFn: () => productionLogsApi.listWaste({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  // Master pickers — shifts and machines used to be free-text UUID
  // inputs. Operators were pasting UUIDs by hand which is brittle and
  // produced silent FK garbage. Loaded from masters now.
  const { data: shifts = [] } = useQuery({
    queryKey: ['shifts-for-logs'],
    queryFn: async () => {
      const r = await apiClient.get('/shifts')
      const raw = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      // Hide deprecated shifts (is_active=false) from the picker — they
      // pollute the dropdown and create logs against shifts that don't
      // actually run anymore.
      return filterActiveShifts(raw)
    },
  })

  const { data: machines = [] } = useQuery({
    queryKey: ['machines-for-logs', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 200 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((m: any) => ({
        id: m.id,
        machine_code: m.machine_code ?? m.machine_id_str ?? '',
        machine_name: m.machine_name ?? m.name ?? '',
        machine_type: m.machine_type ?? m.section ?? '',
      }))
    },
    enabled: !!plantId,
  })

  const shiftOptions = shifts.map((s: any) => ({
    value: s.id,
    label: `${s.shift_code ?? '—'} — ${s.shift_name ?? ''}${s.start_time ? ` (${s.start_time}–${s.end_time})` : ''}`,
  }))

  const machineOptions = machines.map((m: any) => {
    const display = m.machine_name?.trim() || m.machine_code || '—'
    return {
      value: m.id,
      label: `${display}${m.machine_type ? ` · ${m.machine_type}` : ''}`,
    }
  })

  const createLogMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => productionLogsApi.createLog(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-logs-list'] }); message.success('Production log saved'); setOpenLog(false); form.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Save failed'),
  })

  const updateLogMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => productionLogsApi.updateLog(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-logs-list'] }); message.success('Updated'); setOpenLog(false); setEditingLog(null); form.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Update failed'),
  })

  const createBDMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => productionLogsApi.createBreakdown(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['breakdowns-list'] }); message.success('Breakdown recorded'); setOpenBD(false); bdForm.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Save failed'),
  })

  const createWasteMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => productionLogsApi.createWaste(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['waste-records-list'] }); message.success('Waste record saved'); setOpenWaste(false); wasteForm.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Save failed'),
  })

  const logColumns = [
    { title: 'Log No', dataIndex: 'log_no', key: 'no', width: 110 },
    { title: 'Date', dataIndex: 'log_date', key: 'date', width: 110 },
    { title: 'Department', dataIndex: 'department_code', key: 'dept', width: 140, render: (v: string) => v?.replace('_', ' ') },
    { title: 'Planned (kg)', dataIndex: 'planned_output_kg', key: 'plan', align: 'right' as const },
    { title: 'Actual (kg)', dataIndex: 'actual_output_kg', key: 'actual', align: 'right' as const },
    { title: 'Spindle %', dataIndex: 'spindle_utilization_pct', key: 'spindle', align: 'right' as const },
    { title: 'Waste %', dataIndex: 'waste_pct', key: 'waste', align: 'right' as const },
    { title: 'Power/kg', dataIndex: 'power_kwh_per_kg', key: 'power', align: 'right' as const },
    {
      title: 'Verified', dataIndex: 'is_verified', key: 'verified', width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: '', key: 'actions', width: 60,
      render: (_: unknown, r: Record<string, unknown>) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingLog(r); form.setFieldsValue({ ...r, log_date: r.log_date ? dayjs(r.log_date as string) : undefined }); setOpenLog(true) }} />
      ),
    },
  ]

  const bdColumns = [
    { title: 'BD No', dataIndex: 'breakdown_no', key: 'no', width: 110 },
    { title: 'BD Time', dataIndex: 'breakdown_time', key: 'bdt', width: 130, render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY HH:mm') : '' },
    { title: 'Machine', dataIndex: 'machine_name', key: 'machine', width: 160, ellipsis: true },
    { title: 'Category', dataIndex: 'category', key: 'cat', width: 80 },
    { title: 'Duration (min)', dataIndex: 'duration_minutes', key: 'dur', align: 'right' as const, width: 110 },
    { title: 'Repeat', dataIndex: 'is_repeat', key: 'repeat', width: 80, render: (v: boolean) => <Tag color={v ? 'red' : 'default'}>{v ? 'Yes' : 'No'}</Tag> },
    { title: 'Resolved', dataIndex: 'is_resolved', key: 'resolved', width: 80, render: (v: boolean) => <Tag color={v ? 'green' : 'orange'}>{v ? 'Yes' : 'Pending'}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'desc', ellipsis: true },
  ]

  const wasteColumns = [
    { title: 'Record No', dataIndex: 'record_no', key: 'no', width: 120 },
    { title: 'Date', dataIndex: 'log_date', key: 'date', width: 110 },
    { title: 'Department', dataIndex: 'department_code', key: 'dept', width: 140, render: (v: string) => v?.replace('_', ' ') },
    { title: 'Waste Type', dataIndex: 'waste_type', key: 'type', width: 110 },
    { title: 'Weight (kg)', dataIndex: 'weight_kg', key: 'wt', align: 'right' as const },
    { title: 'Waste %', dataIndex: 'waste_pct', key: 'pct', align: 'right' as const },
    { title: 'Standard %', dataIndex: 'standard_pct', key: 'std', align: 'right' as const },
    { title: 'Variance %', dataIndex: 'variance_pct', key: 'var', align: 'right' as const,
      render: (v: string) => v ? <span style={{ color: parseFloat(v) > 0 ? '#cf1322' : '#389e0d' }}>{v}%</span> : '' },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ThunderboltOutlined style={{ marginRight: 8, color: '#faad14' }} />
        Production Operations
      </Title>

      <Tabs
        defaultActiveKey="logs"
        style={{ marginTop: 16 }}
        tabBarExtraContent={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingLog(null); form.resetFields(); form.setFieldsValue({ log_date: dayjs() }); setOpenLog(true) }}>
              Production Log
            </Button>
            <Button icon={<AlertOutlined />} danger onClick={() => { bdForm.resetFields(); bdForm.setFieldsValue({ breakdown_time: dayjs(), category: 'M', is_repeat: false }); setOpenBD(true) }}>
              Log Breakdown
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => { wasteForm.resetFields(); wasteForm.setFieldsValue({ log_date: dayjs() }); setOpenWaste(true) }}>
              Waste Record
            </Button>
          </Space>
        }
        items={[
          {
            key: 'logs',
            label: 'Production Logs',
            children: (
              <Card>
                <Table dataSource={Array.isArray(logs) ? logs : []} columns={logColumns} rowKey="id" loading={logsLoading} size="small" scroll={{ x: 1100 }} pagination={{ pageSize: 20 }} />
              </Card>
            ),
          },
          {
            key: 'breakdowns',
            label: 'Machine Breakdowns',
            children: (
              <Card>
                <Table dataSource={Array.isArray(breakdowns) ? breakdowns : []} columns={bdColumns} rowKey="id" loading={bdLoading} size="small" pagination={{ pageSize: 20 }} />
              </Card>
            ),
          },
          {
            key: 'waste',
            label: 'Waste Records',
            children: (
              <Card>
                <Table dataSource={Array.isArray(wasteRecords) ? wasteRecords : []} columns={wasteColumns} rowKey="id" loading={wasteLoading} size="small" pagination={{ pageSize: 20 }} />
              </Card>
            ),
          },
        ]}
      />

      {/* Production Log Drawer */}
      <Drawer
        title={editingLog ? 'Edit Production Log' : 'New Production Log'}
        open={openLog}
        onClose={() => { setOpenLog(false); setEditingLog(null); form.resetFields() }}
        width={720}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setOpenLog(false); setEditingLog(null); form.resetFields() }}>Cancel</Button>
            <Button type="primary" loading={createLogMut.isPending || updateLogMut.isPending}
              onClick={() => form.validateFields().then(vals => {
                const payload = { ...vals, plant_id: plantId, log_date: vals.log_date?.format('YYYY-MM-DD') }
                if (editingLog) updateLogMut.mutate({ id: editingLog.id as string, data: payload })
                else createLogMut.mutate(payload)
              })}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}><Form.Item name="log_date" label="Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" /></Form.Item></Col>
            <Col span={8}><Form.Item name="department_code" label="Department" rules={[{ required: true }]}>
              <Select>{DEPARTMENTS.map(d => <Select.Option key={d} value={d}>{d.replace('_', ' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={8}><Form.Item name="shift_id" label="Shift" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={shiftOptions}
                placeholder={shiftOptions.length === 0 ? 'No shifts configured — add in HR → Shifts' : 'Pick a shift'}
                notFoundContent="No shifts configured. Add them in HR → Shifts."
              />
            </Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="planned_output_kg" label="Planned (kg)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="actual_output_kg" label="Actual (kg)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="installed_machines" label="Installed Machines"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="running_machines" label="Running Machines"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="installed_spindles" label="Installed Spindles"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="running_spindles" label="Running Spindles"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="total_stoppage_minutes" label="Total Stoppage (min)"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="mechanical_stoppage_min" label="Mechanical Stop (min)"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="electrical_stoppage_min" label="Electrical Stop (min)"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="total_waste_kg" label="Total Waste (kg)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="actual_headcount" label="Headcount"><InputNumber style={{ width: '100%' }} precision={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="power_kwh_consumed" label="Power (kWh)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="quality_rejections_kg" label="Quality Rej (kg)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="end_breaks_per_1000_spindle_hrs" label="End Breaks/1000 spindle hrs"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="is_verified" label="Verified" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Drawer>

      {/* Breakdown Drawer */}
      <Drawer
        title="Log Machine Breakdown"
        open={openBD}
        onClose={() => { setOpenBD(false); bdForm.resetFields() }}
        width={560}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setOpenBD(false); bdForm.resetFields() }}>Cancel</Button>
            <Button type="primary" danger loading={createBDMut.isPending}
              onClick={() => bdForm.validateFields().then(vals => {
                createBDMut.mutate({ ...vals, plant_id: plantId, breakdown_time: vals.breakdown_time?.toISOString() })
              })}>
              Record Breakdown
            </Button>
          </Space>
        }
      >
        <Form form={bdForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="machine_id" label="Machine" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={machineOptions}
                placeholder={machineOptions.length === 0 ? 'No machines for this plant — add in Masters → Machines' : 'Pick a machine'}
                notFoundContent="No active machines. Add them in Masters → Machines."
              />
            </Form.Item></Col>
            <Col span={12}><Form.Item name="breakdown_time" label="Breakdown Time" rules={[{ required: true }]}><DatePicker showTime style={{ width: '100%' }} format="DD-MMM-YYYY HH:mm" /></Form.Item></Col>
            <Col span={12}><Form.Item name="category" label="Category" rules={[{ required: true }]}>
              <Select>{BD_CATEGORIES.map(c => <Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={12}><Form.Item name="is_repeat" label="Repeat Breakdown" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="spare_used" label="Spare Part Used"><Input /></Form.Item>
        </Form>
      </Drawer>

      {/* Waste Record Drawer */}
      <Drawer
        title="Record Waste"
        open={openWaste}
        onClose={() => { setOpenWaste(false); wasteForm.resetFields() }}
        width={480}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setOpenWaste(false); wasteForm.resetFields() }}>Cancel</Button>
            <Button type="primary" loading={createWasteMut.isPending}
              onClick={() => wasteForm.validateFields().then(vals => {
                createWasteMut.mutate({ ...vals, plant_id: plantId, log_date: vals.log_date?.format('YYYY-MM-DD') })
              })}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={wasteForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="log_date" label="Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" /></Form.Item></Col>
            <Col span={12}><Form.Item name="department_code" label="Department" rules={[{ required: true }]}>
              <Select>{DEPARTMENTS.map(d => <Select.Option key={d} value={d}>{d.replace('_', ' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={12}><Form.Item name="waste_type" label="Waste Type" rules={[{ required: true }]}>
              <Select>{WASTE_TYPES.map(w => <Select.Option key={w.value} value={w.value}>{w.label}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={12}><Form.Item name="weight_kg" label="Weight (kg)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="input_kg" label="Input (kg)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="standard_pct" label="Standard %"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input /></Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
